import {
  Button,
  FileInput,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  useAddBelt,
  useAddOps,
  useBelt,
  useDeleteBelt,
  useDeleteOps,
  useImportBelt,
  useOperationsLog,
} from "../../api/generated/stockvol";
import type { OpsInReason, PageBeltOut, PageOpsLogOut } from "../../api/generated/model";
import { useSiteInfo } from "../../api/hooks";
import { act, confirm } from "../../lib/actions";
import { formatLocal, localToUtcIso, toLocalInput } from "../../lib/time";
import { formatBearing, scaleOf } from "../../lib/units";
import { IconClipboardList, IconFlask, IconScale } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Section, TableCard } from "../../components/Section";
import { LabResults } from "./LabResults";

const data = <T,>(r: unknown) => (r as { data: T }).data;
const REASONS: OpsInReason[] = ["pile_full", "feed_changed", "product_changed", "maintenance", "reclaim", "other"];

/** The operations log, the belt tonnes (M6.6) and the lab results (M7.9): entries, CSV import,
 * deletions. */
export default function OperationsPage() {
  return (
    <Stack gap="lg">
      <PageHeader
        title="Operations"
        subtitle="What the stacker and the loaders did, the belt-scale tonnes and the lab results: they explain each change, calibrate the density and give each layer its coal quality"
      />
      <Tabs defaultValue="log" keepMounted={false} variant="pills" radius="md">
        <Tabs.List>
          <Tabs.Tab value="log" leftSection={<IconClipboardList size={16} />}>
            Operations log
          </Tabs.Tab>
          <Tabs.Tab value="belt" leftSection={<IconScale size={16} />}>
            Belt tonnes
          </Tabs.Tab>
          <Tabs.Tab value="lab" leftSection={<IconFlask size={16} />}>
            Lab results
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="log" pt="md">
          <OpsLog />
        </Tabs.Panel>
        <Tabs.Panel value="belt" pt="md">
          <Belt />
        </Tabs.Panel>
        <Tabs.Panel value="lab" pt="md">
          <LabResults />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function OpsLog() {
  const site = useSiteInfo();
  const tz = site.data?.timezone ?? "UTC";
  const qc = useQueryClient();
  const log = useOperationsLog({ limit: 100 }, { query: { select: data<PageOpsLogOut> } });
  const add = useAddOps();
  const del = useDeleteOps();
  const [start, setStart] = useState(() => toLocalInput(Date.now(), tz));
  const [end, setEnd] = useState("");
  const [unit, setUnit] = useState<"deg" | "m">("m");
  const [from, setFrom] = useState<number | string>("");
  const [to, setTo] = useState<number | string>("");
  const [reason, setReason] = useState<OpsInReason>("reclaim");
  const [product, setProduct] = useState("");
  const [note, setNote] = useState("");
  const num = (v: number | string) => (v === "" ? null : Number(v));
  const submit = () =>
    void act(
      () =>
        add.mutateAsync({
          data: {
            t_start: localToUtcIso(start, tz),
            t_end: end ? localToUtcIso(end, tz) : null,
            ...(unit === "deg"
              ? { slew_from_deg: num(from), slew_to_deg: num(to) }
              : { position_from_m: num(from), position_to_m: num(to) }),
            reason,
            product: product || null,
            note: note || null,
          },
        }),
      { done: "Entry added", qc, refresh: ["operations"] },
    );
  const scale = site.data ? scaleOf(site.data) : null;
  return (
    <Stack gap="lg">
      <Section title="New entry">
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <TextInput type="datetime-local" label={`From (${tz})`} value={start} onChange={(e) => setStart(e.currentTarget.value)} />
        <TextInput type="datetime-local" label="To (optional)" value={end} onChange={(e) => setEnd(e.currentTarget.value)} />
        <Select label="Reason" data={REASONS} value={reason} onChange={(v) => setReason((v ?? "other") as OpsInReason)} />
        <Group grow align="flex-end">
          <SegmentedControl
            value={unit}
            onChange={(v) => setUnit(v as "deg" | "m")}
            data={[
              { label: "position m", value: "m" },
              { label: "slew °", value: "deg" },
            ]}
          />
        </Group>
        <NumberInput label={`Where: from (${unit === "m" ? "m" : "°"})`} value={from} onChange={setFrom} decimalScale={2} />
        <NumberInput label={`to (${unit === "m" ? "m" : "°"})`} value={to} onChange={setTo} decimalScale={2} />
        <TextInput label="Product" value={product} onChange={(e) => setProduct(e.currentTarget.value)} />
        <TextInput label="Note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group align="flex-end">
          <Button onClick={submit} loading={add.isPending}>
            Add entry
          </Button>
        </Group>
      </SimpleGrid>
      </Section>
      <TableCard title="Entries">
      <Table highlightOnHover fz="sm" data-testid="ops-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>From</Table.Th>
            <Table.Th>To</Table.Th>
            <Table.Th>Where</Table.Th>
            <Table.Th>Reason</Table.Th>
            <Table.Th>Product</Table.Th>
            <Table.Th>Note</Table.Th>
            <Table.Th>By</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {log.data?.items.map((e) => (
            <Table.Tr key={e.id}>
              <Table.Td>{formatLocal(e.t_start, tz)}</Table.Td>
              <Table.Td>{e.t_end ? formatLocal(e.t_end, tz) : "—"}</Table.Td>
              <Table.Td>
                {e.position_from_m !== null ? `${e.position_from_m.toFixed(1)}–${e.position_to_m?.toFixed(1) ?? "?"} m` : "—"}
                {e.slew_from_deg !== null && scale ? (
                  <Text span c="dimmed" size="xs">
                    {" "}
                    ({formatBearing(e.slew_from_deg)}
                    {e.slew_to_deg !== null ? `–${formatBearing(e.slew_to_deg)}` : ""})
                  </Text>
                ) : null}
              </Table.Td>
              <Table.Td>{e.reason}</Table.Td>
              <Table.Td>{e.product ?? ""}</Table.Td>
              <Table.Td>{e.note ?? ""}</Table.Td>
              <Table.Td>{e.created_by ?? ""}</Table.Td>
              <Table.Td>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    confirm("Delete entry", "Remove this operations-log entry?", () =>
                      void act(() => del.mutateAsync({ entryId: e.id }), { done: "Entry removed", qc, refresh: ["operations"] }),
                    )
                  }
                >
                  Delete
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </TableCard>
    </Stack>
  );
}

function Belt() {
  const site = useSiteInfo();
  const tz = site.data?.timezone ?? "UTC";
  const qc = useQueryClient();
  const belt = useBelt({ limit: 100 }, { query: { select: data<PageBeltOut> } });
  const add = useAddBelt();
  const imp = useImportBelt();
  const del = useDeleteBelt();
  const [start, setStart] = useState(() => toLocalInput(Date.now() - 3_600_000, tz));
  const [end, setEnd] = useState(() => toLocalInput(Date.now(), tz));
  const [tonnes, setTonnes] = useState<number | string>("");
  const [file, setFile] = useState<File | null>(null);
  return (
    <Stack gap="lg">
      <Section title="Add a reading or import a CSV">
      <Stack>
      <SimpleGrid cols={{ base: 1, sm: 4 }}>
        <TextInput type="datetime-local" label={`From (${tz})`} value={start} onChange={(e) => setStart(e.currentTarget.value)} />
        <TextInput type="datetime-local" label="To" value={end} onChange={(e) => setEnd(e.currentTarget.value)} />
        <NumberInput label="Tonnes" value={tonnes} onChange={setTonnes} min={0} />
        <Group align="flex-end">
          <Button
            loading={add.isPending}
            disabled={tonnes === ""}
            onClick={() =>
              void act(
                () =>
                  add.mutateAsync({
                    data: { t_start: localToUtcIso(start, tz), t_end: localToUtcIso(end, tz), tonnes_t: Number(tonnes) },
                  }),
                { done: "Belt reading added", qc, refresh: ["belt"] },
              )
            }
          >
            Add
          </Button>
        </Group>
      </SimpleGrid>
      <Group align="flex-end">
        <FileInput
          label="Import a CSV (t_start,t_end,tonnes_t; ISO times)"
          accept=".csv,text/csv"
          value={file}
          onChange={setFile}
          w={360}
        />
        <Button
          variant="light"
          disabled={!file}
          loading={imp.isPending}
          onClick={() =>
            file &&
            confirm("Import belt readings", `Import every row of ${file.name}? A bad row stops the whole import.`, () =>
              void act(() => imp.mutateAsync({ data: { file } }), { done: `${file.name} imported`, qc, refresh: ["belt"] }),
            )
          }
        >
          Import
        </Button>
      </Group>
      </Stack>
      </Section>
      <TableCard title="Belt readings">
      <Table highlightOnHover fz="sm" data-testid="belt-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>From</Table.Th>
            <Table.Th>To</Table.Th>
            <Table.Th ta="right">Tonnes</Table.Th>
            <Table.Th>Source</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {belt.data?.items.map((b) => (
            <Table.Tr key={b.id}>
              <Table.Td>{formatLocal(b.t_start, tz)}</Table.Td>
              <Table.Td>{formatLocal(b.t_end, tz)}</Table.Td>
              <Table.Td ta="right">{b.tonnes_t.toLocaleString("en-US", { maximumFractionDigits: 1 })}</Table.Td>
              <Table.Td>{b.source}</Table.Td>
              <Table.Td>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    confirm("Delete reading", "Remove this belt reading?", () =>
                      void act(() => del.mutateAsync({ readingId: b.id }), { done: "Reading removed", qc, refresh: ["belt"] }),
                    )
                  }
                >
                  Delete
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </TableCard>
    </Stack>
  );
}
